import uniqBy from 'lodash/uniqBy';
import { HttpLink, ApolloClient, ApolloLink, InMemoryCache, createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { TokenAuthStorageSingleton } from '@syconium/iron-figs';
import { stripIgnoredCharacters } from 'graphql/utilities/stripIgnoredCharacters';
import { getFigsEnvironment } from './shop-context/figs-env.mjs';
export { figsEnvironments, figsIndividualEnvironments, figsSharedEnvironments, isFigsEnvironment, isFigsIndividualEnvironment, isFigsSharedEnvironment } from './shop-context/figs-env.mjs';
import { retrieveRegion, retrieveLocale, isValidCountryCode, supportedRegions, retrieveLanguage } from './shop-context/regions.mjs';
export { defaultLocale, isValidLanguageGroup, isValidLocale, retrieveEnabledLocales, retrieveLanguageGroupsForRegion, retrieveLanguageRegionPairs, retrieveLocalesForRegion, retrieveRegions, supportedLocaleIdentifiers } from './shop-context/regions.mjs';
export { parseAppSourceFromUserAgent } from './user-agent.mjs';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { sha256 } from 'crypto-hash';
import { print } from 'graphql/language/printer';

const mergeArrayByIndex = (existing, incoming, mergeObjects) => {
  return incoming.map((item, index) => {
    const previous = existing[index];
    return previous ? mergeObjects(previous, item) : item;
  });
};
const mergeFactory = (itemsField) => {
  return (existing, incoming) => {
    if (!existing)
      return incoming;
    const existingItems = existing[itemsField] || [];
    const incomingItems = incoming[itemsField] || [];
    return {
      ...incoming,
      [itemsField]: [...existingItems, ...incomingItems]
    };
  };
};

const catalogCacheConfig = {
  typePolicies: {
    Query: {
      fields: {
        shopOrders: {
          keyArgs: ["id"],
          merge: mergeFactory("shopOrders")
        },
        collectionGridSection: {
          keyArgs: ["id"],
          read: (existing, options) => {
            if (existing)
              return existing;
            const collectionGridFieldCacheKey = `CollectionGridSection:${options.args?.id ?? ""}`;
            const cachedCollectionGridField = options.cache.extract()[collectionGridFieldCacheKey];
            if (cachedCollectionGridField)
              return { __ref: collectionGridFieldCacheKey };
            return void 0;
          }
        }
      }
    },
    CollectionView: {
      fields: {
        seoContent: {
          merge: true
        }
      }
    },
    Product: {
      fields: {
        colorInfo: {
          merge: true
        },
        fitModel: {
          merge: true
        },
        review: {
          merge: true
        }
      },
      keyFields: (product) => {
        const { __typename, id } = product;
        const identifier = (() => {
          if (id)
            return id;
          return Math.random().toString();
        })();
        return `${__typename}:${identifier}`;
      }
    },
    SearchContext: {
      fields: {
        items: {
          keyArgs: false,
          merge(existing, incoming) {
            const existingNodes = existing?.nodes ?? [];
            const allItemNodes = uniqBy([...existingNodes, ...incoming.nodes], (node) => {
              if ("productInfo" in node)
                return node.productInfo.__ref;
              if ("product" in node)
                return node.product.__ref;
              return node.__ref;
            });
            return {
              ...incoming,
              nodes: allItemNodes
            };
          }
        }
      }
    },
    Variant: {
      // Variants are able to possibly return different values for their uniquely identifiable `sku` & `shopifyId` fields
      // depending on if the new collection service is being used or not. Disabling normalization on variants while enabling
      // object merges ensures that we don't see any cache inconsistencies when storing cache data between the two services.
      keyFields: false,
      merge: true
    },
    CollectionGridSection: {
      // TODO: We should look into this more. This is here just because without it,
      // hovering a tile on the PLP causes the PLP paging to break. But it shouldn't
      // require globally altering CollectionGridSection. I would think there is just some
      // specific nested fields within it that need some merge guidance.
      merge: true
    },
    Collection: {
      fields: {
        items: {
          keyArgs: ["selectedFilters", "grouping", "first"],
          merge(existing, incoming, opts) {
            const existingNodes = existing?.nodes ?? [];
            if (opts.args && !opts.args.after && existingNodes.length > 0)
              return incoming;
            const incomingNodes = incoming?.nodes ?? [];
            const allItemNodes = uniqBy([...existingNodes, ...incomingNodes], (node) => node.__ref);
            return {
              ...incoming,
              nodes: allItemNodes
            };
          }
        }
      }
    },
    ProductGroup: {
      fields: {
        sizeChart: {
          merge: true
        },
        sizeChartCG: {
          merge: true
        },
        priceRange: {
          merge: true
        },
        variants: {
          merge(existing, incoming) {
            const existingNodes = existing?.nodes ?? [];
            const incomingNodes = incoming?.nodes ?? [];
            const allItemNodes = uniqBy([...existingNodes, ...incomingNodes], (node) => node.__ref);
            return {
              ...incoming,
              nodes: allItemNodes
            };
          }
        }
      },
      keyFields: (productGroup) => {
        const { __typename, collectionHandle, id } = productGroup;
        const identifier = (() => {
          if (collectionHandle && id)
            return `${collectionHandle}:${id}`;
          if (id)
            return id;
          return Math.random().toString();
        })();
        return `${__typename}:${identifier}`;
      }
    },
    Customer: {
      fields: {
        profile: {
          merge: true
        },
        portal: {
          merge: true
        },
        defaultAddress: {
          merge: true
        }
      }
    },
    CustomerPersonalizationProfile: {
      fields: {
        embroideryPreferences: {
          merge: true
        }
      }
    },
    SiteFixtures: {
      fields: {
        navigation: {
          merge: true
        }
      }
    },
    ProductCatalogEmbroideryConfiguration: {
      keyFields: false,
      merge: true
    },
    ProductCatalogEmbroideryIconOption: {
      keyFields: false,
      merge: true
    },
    ProductCatalogEmbroideryLogoOption: {
      keyFields: false,
      merge: true
    },
    ProductCatalogEmbroideryTextOption: {
      keyFields: false,
      merge: true
    },
    ProductCatalogImage: {
      keyFields: false,
      merge: true
    },
    ProductCatalogPriceRange: {
      keyFields: false,
      merge: true
    },
    ProductCatalogCompareAtPriceRange: {
      keyFields: false,
      merge: true
    },
    ProductCatalogPrice: {
      keyFields: false,
      merge: true
    },
    ProductCatalogColor: {
      keyFields: false,
      // ProductCatalogColor is adaptive and has swatch url overrides per variant group. Do not share.
      merge: true
    },
    ProductCatalogKitComponent: {
      keyFields: false,
      // Kit components lack IDs, they are unique to the kit they are in. Do not share.
      merge: true
    },
    ProductCatalogFitModel: {
      keyFields: false,
      // Fit model ids reflect the model itself, not the color+model combination. Do not allow sharing until the BE can fix the IDs.
      merge: true
    },
    ProductCatalogProductColorOption: {
      keyFields: false,
      // Colors options can return specific price/swatch info to the product they are being queried within. Do not allow sharing.
      merge: true,
      fields: {
        priceRange: {
          merge: true
        },
        compareAtPriceRange: {
          merge: true
        }
      }
    },
    ProductCatalogProductOptions: {
      keyFields: false,
      merge: true
    },
    ProductCatalogProductOption: {
      fields: {
        options: {
          merge: true
        }
      }
    },
    ProductCatalogStandardProduct: {
      keyFields: false,
      // Our standard product model contains business logic for options and priceRanges fields that make it differ based on its sorrounding vg/collection.
      merge: true,
      fields: {
        options: {
          merge: true
        },
        priceRanges: {
          merge: true
        }
      }
    },
    ProductCatalogKit: {
      fields: {
        options: {
          merge: true
        },
        priceRanges: {
          merge: true
        }
      }
    },
    ProductCatalogVariantGroup: {
      fields: {
        product: {
          merge: true
        },
        defaultVariant: {
          merge: true
        },
        primaryCategory: {
          merge: true
        },
        priceRange: {
          merge: true
        },
        compareAtPriceRange: {
          merge: true
        },
        plpBadge: {
          merge: true
        },
        iconBadge: {
          merge: true
        },
        embroidery: {
          merge: true
        }
      }
    },
    ProductCatalogVariant: {
      fields: {
        price: {
          merge: true
        },
        compareAtPrice: {
          merge: true
        }
      }
    },
    ProductCatalogCollection: {
      fields: {
        items: {
          keyArgs: ["filters", "first", "after"],
          // `merge: true` only spreads the connection's own keys, so `pageInfo` and the two
          // arrays were still replaced wholesale by whichever query wrote last — dropping any
          // field the newer, narrower selection didn't ask for. Merge the contents instead.
          //
          // Every argument `items` accepts is in keyArgs above, so a merge only ever reconciles
          // two writes of the same page of the same query. That is what makes pairing the array
          // elements up by index safe here.
          merge(existing, incoming, { mergeObjects }) {
            if (!existing)
              return incoming;
            const merged = { ...existing, ...incoming };
            if (existing.pageInfo && incoming.pageInfo) {
              merged.pageInfo = mergeObjects(existing.pageInfo, incoming.pageInfo);
            }
            if (existing.filters && incoming.filters) {
              merged.filters = mergeArrayByIndex(existing.filters, incoming.filters, mergeObjects);
            }
            if (existing.nodes && incoming.nodes) {
              merged.nodes = mergeArrayByIndex(existing.nodes, incoming.nodes, mergeObjects);
            }
            return merged;
          }
        }
      }
    }
  }
};

const figsFetch = (input, init) => {
  if (init?.method === "GET") {
    deleteHeader(init.headers, "content-type");
  }
  if (isRequest(input)) {
    return fetch(input, init);
  }
  const url = urlWithRelativeSupportAndQueryStripping(input);
  return fetch(url.toString(), init);
};
function isRequest(info) {
  return !!info.url;
}
function deleteHeader(headers, key) {
  if (!headers)
    return;
  if (isHeaders(headers))
    headers.delete(key);
  else if (!Array.isArray(headers))
    delete headers[key];
}
function isHeaders(headers) {
  return !Array.isArray(headers) && !!headers.delete;
}
function urlWithRelativeSupportAndQueryStripping(info) {
  let url;
  if (typeof info === "string") {
    const requestUriIsRelative = info.startsWith("/");
    if (requestUriIsRelative) {
      url = new URL(info, globalThis.location?.origin);
    } else {
      url = new URL(info);
    }
  } else {
    url = new URL(info);
  }
  const query = url.searchParams.get("query");
  if (query) {
    url.searchParams.set("query", stripIgnoredCharacters(query));
  }
  return url.toString();
}

const initClient = ({
  host,
  inMemoryCacheConfig,
  useGETForQueries = true,
  headers = void 0
}) => {
  const httpLink = new HttpLink({
    uri: new URL("graphql", host).toString(),
    useGETForQueries,
    fetch: figsFetch,
    fetchOptions: {
      mode: "cors"
    },
    headers
  });
  const authLink = setContext((_, { headers: headers2 }) => {
    const token = TokenAuthStorageSingleton.getInstance().getToken();
    if (!token) {
      return { headers: headers2 };
    }
    return {
      headers: {
        ...headers2,
        authorization: token
      }
    };
  });
  const activeLinks = [authLink, httpLink];
  const client = new ApolloClient({
    link: ApolloLink.from(activeLinks),
    cache: new InMemoryCache(inMemoryCacheConfig),
    ssrMode: typeof window === "undefined" || typeof document === "undefined",
    defaultOptions: {
      watchQuery: {
        errorPolicy: "none"
      },
      query: {
        errorPolicy: "all"
      },
      mutate: {
        errorPolicy: "none"
      }
    }
  });
  return client;
};

function consoleTrace(scope, message, ...args) {
  console.debug(`[${scope}]`, message, ...args);
}
const traceEnabled = process.env.NEXT_PUBLIC_CLIENT_TRACE_ENABLED === "true";
const trace = traceEnabled ? consoleTrace : () => void 0;

function cacheKeyScope(ctx) {
  return `${ctx.region}:${ctx.currency}:${ctx.locale}${ctx.name}`;
}
function cacheKeyFor(ctx, profile) {
  return `${cacheKeyScope(ctx)}:${profile}`;
}
function selectClients(cache, ctx) {
  const scopePrefix = cacheKeyScope(ctx);
  const clients = [];
  cache.forEach((c, k) => {
    if (k.startsWith(scopePrefix))
      clients.push([k, c]);
  });
  return clients;
}
function cachedClientOr(cache, profile, ctx, create) {
  const key = cacheKeyFor(ctx, profile);
  const hit = cache.get(key);
  trace("client cache hit for", key, !!hit);
  if (hit)
    return hit;
  const client = create();
  cache.set(key, client);
  return client;
}

function figsAuthLink() {
  return setContext((_, { headers }) => {
    const token = TokenAuthStorageSingleton.getInstance().getToken();
    if (!token) {
      return { headers };
    }
    return {
      headers: {
        ...headers,
        authorization: token
      }
    };
  });
}

function figsHttpLink(baseUrl, graph) {
  const baseUrlOrUri = baseUrl === "/" ? `/${graph}/graphql` : process.env.NEXT_PUBLIC_LOCAL_BASE_URL === "true" ? new URL(`graphql`, baseUrl).toString() : new URL(`${graph}/graphql`, baseUrl).toString();
  return createHttpLink({
    useGETForQueries: true,
    fetch: figsFetch,
    fetchOptions: {
      mode: "cors"
    },
    uri: baseUrlOrUri
  });
}

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
const defaultBuildServiceFilterHeaderArgs = {
  includeUnavailable: false,
  isPreview: false,
  launchTags: void 0
};
const _ServiceFilterPairs = class _ServiceFilterPairs {
  constructor() {
  }
  static buildServiceFilterHeader({
    includeUnavailable = defaultBuildServiceFilterHeaderArgs.includeUnavailable,
    isPreview = defaultBuildServiceFilterHeaderArgs.isPreview,
    launchTags = defaultBuildServiceFilterHeaderArgs.launchTags,
    customerTier = defaultBuildServiceFilterHeaderArgs.customerTier,
    releaseDate = defaultBuildServiceFilterHeaderArgs.releaseDate
  } = defaultBuildServiceFilterHeaderArgs) {
    const headerPairs = [];
    if (includeUnavailable || this.unavailableProducts) {
      headerPairs.push(_ServiceFilterPairs.IncludeUnavailableProductDataPair);
    }
    if (isPreview || this.previewContent) {
      headerPairs.push(_ServiceFilterPairs.PreviewContentPair);
    }
    if (launchTags?.length || this.launchTags?.length) {
      const tags = this.launchTags?.length ? this.launchTags : launchTags;
      headerPairs.push(`launch-tags=${tags}`);
    }
    if (customerTier || this.customerTier) {
      headerPairs.push(`customer-tier=${customerTier ?? this.customerTier}`);
    }
    if (releaseDate || this.releaseDate) {
      headerPairs.push(`date=${releaseDate ?? this.releaseDate}`);
    }
    if (headerPairs.length === 0)
      return;
    return headerPairs.join("; ");
  }
  static includePreviewContent() {
    this.previewContent = true;
  }
  static includeUnavailableProducts() {
    this.unavailableProducts = true;
  }
  static includeLaunchTags(tags) {
    this.launchTags = tags;
  }
  static includeCustomerTier(customerTier) {
    this.customerTier = customerTier;
  }
  static includeReleaseDate(releaseDate) {
    this.releaseDate = releaseDate;
  }
};
__publicField(_ServiceFilterPairs, "PreviewContentPair", "content=preview");
__publicField(_ServiceFilterPairs, "IncludeUnavailableProductDataPair", "product-data=include-unavailable");
__publicField(_ServiceFilterPairs, "unavailableProducts", false);
__publicField(_ServiceFilterPairs, "previewContent", false);
__publicField(_ServiceFilterPairs, "launchTags");
__publicField(_ServiceFilterPairs, "customerTier");
__publicField(_ServiceFilterPairs, "releaseDate");
let ServiceFilterPairs = _ServiceFilterPairs;

function figsServiceSelectionLink(serviceFilterPairsContext) {
  return setContext((_request, ctx) => {
    const headers = ctx.headers || {};
    const serviceFilterHeader = ServiceFilterPairs.buildServiceFilterHeader({
      includeUnavailable: ctx.includeUnavailable ?? serviceFilterPairsContext?.includeUnavailable,
      isPreview: ctx.isPreview ?? serviceFilterPairsContext?.isPreview,
      launchTags: ctx.launchTags ?? serviceFilterPairsContext?.launchTags,
      customerTier: ctx.customerTier ?? serviceFilterPairsContext?.customerTier,
      releaseDate: ctx.releaseDate ?? serviceFilterPairsContext?.releaseDate
    });
    if (serviceFilterHeader) {
      headers["x-figs-service-filter"] = serviceFilterHeader;
    }
    return { ...ctx, headers };
  });
}

const shopAddressabilityConfig = {
  defaultDomainPrefix: process.env.NEXT_PUBLIC_DEFAULT_INGRESS_DOMAIN_PREFIX || "www",
  implicitEnvList: ["prod"],
  selfContainedEnvList: ["local", "live"],
  ingressBaseDomain: process.env.NEXT_PUBLIC_INGRESS_BASE_DOMAIN || "wearfigs.com"
};
function shopCookieDomain() {
  const figsEnvironment = getFigsEnvironment();
  if (shopAddressabilityConfig.selfContainedEnvList.includes(figsEnvironment))
    return void 0;
  const implicitEnv = shopAddressabilityConfig.implicitEnvList.includes(figsEnvironment);
  let subdomain = implicitEnv ? "" : `${figsEnvironment}.`;
  return `.${subdomain.toLocaleLowerCase()}${shopAddressabilityConfig.ingressBaseDomain}`;
}
const cookieDefaultMaxAge = 26e5;
const cookieDefaultOptions = {
  path: "/",
  maxAge: cookieDefaultMaxAge,
  httpOnly: false,
  domain: shopCookieDomain()
};
function requestedCookieContext(cookies) {
  if (!cookies) {
    return {};
  }
  return {
    region: cookies.get(regionKey),
    locale: cookies.get(localeKey),
    currency: cookies.get(currencyKey)
  };
}

function serializeContextToHeaderTarget(ctx, target) {
  const serialized = serializeContext(ctx);
  for (const [key, value] of Object.entries(serialized)) {
    target.header(key, value);
  }
}
function requestedHeaderContext(headers) {
  const response = {};
  if (!headers)
    return response;
  response.currency = oneOf(headers[currencyKey]);
  response.name = oneOf(headers[nameKey]);
  const regionHeader = oneOf(headers[regionKey]);
  const localeHeader = oneOf(headers[localeKey]);
  const supportedRegion = retrieveRegion({ countryCode: regionHeader, defaultFallback: false });
  if (supportedRegion) {
    response.region = supportedRegion.id;
    const supportedLocale = retrieveLocale({
      region: supportedRegion,
      locale: localeHeader,
      defaultFallback: false
    });
    if (supportedLocale) {
      response.locale = supportedLocale;
    }
  }
  if (!response.region) {
    const locationBasedHeader = oneOf(headers[ingressDetectedCountryKey]);
    const locationBasedRegion = isValidCountryCode(locationBasedHeader) ? retrieveRegion({
      isEnabled: true,
      countryCode: locationBasedHeader,
      defaultFallback: false
    }) : null;
    if (locationBasedRegion) {
      response.region = locationBasedRegion?.id;
      const supportedLocale = retrieveLocale({
        region: locationBasedRegion,
        locale: localeHeader,
        defaultFallback: false
      });
      if (supportedLocale) {
        response.locale = supportedLocale;
      }
    }
  }
  return response;
}
function oneOf(headerValue) {
  if (Array.isArray(headerValue)) {
    return headerValue[0];
  }
  return headerValue;
}

function requestedPathParamContext(urlPath) {
  const firstPathParam = urlPath?.split("/")[1];
  const languageCountryPair = firstPathParam;
  if (languageCountryPair) {
    const language = languageCountryPair.split("-")[0].toLocaleLowerCase();
    const country = languageCountryPair.split("-").reverse()[0].toUpperCase();
    const supportedRegion = retrieveRegion({ countryCode: country, defaultFallback: false });
    if (supportedRegion) {
      const supportedLocale = retrieveLocale({
        language,
        region: supportedRegion,
        defaultFallback: false
      });
      if (supportedLocale) {
        return {
          region: supportedRegion.id,
          locale: supportedLocale,
          currency: supportedRegion.currency
        };
      }
    }
  }
  return {};
}

const prefix = "x-figs-shop";
const ingressDetectedCountryKey = "cloudfront-viewer-country";
const regionKey = `${prefix}-region`;
const localeKey = `${prefix}-locale`;
const currencyKey = `${prefix}-currency`;
const nameKey = `${prefix}-name`;
const legacyLocaleKey = "userLocale";
const detectedCountryEchoKey = `${prefix}-detected-country`;
function serializeContext(ctx) {
  return {
    [regionKey]: ctx.region,
    [localeKey]: ctx.locale,
    [currencyKey]: ctx.currency,
    ...ctx.name && {
      [nameKey]: ctx.name
    }
  };
}
function buildContextForWebsitePageView(pageViewContext) {
  const shopName = process.env.NEXT_PUBLIC_SHOPIFY_SHOP_NAME;
  const pathParams = requestedPathParamContext(pageViewContext.urlPath);
  const cookies = requestedCookieContext(pageViewContext.cookies);
  const headers = requestedHeaderContext(pageViewContext.headers);
  const requestedRegionId = pathParams.region ?? cookies.region ?? headers.region ?? "";
  const requestedLocale = pathParams.locale ?? cookies.locale ?? headers.locale;
  const region = isValidCountryCode(requestedRegionId) ? supportedRegions[requestedRegionId] : supportedRegions.US;
  const locale = retrieveLocale({
    region,
    locale: requestedLocale,
    defaultFallback: true
  });
  const language = retrieveLanguage({
    region,
    locale,
    defaultFallback: true
  });
  return {
    region: region.id,
    locale,
    language,
    currency: region.currency,
    name: shopName
  };
}
function buildContextForApiRequestHandler(apiRequestContext) {
  const headers = requestedHeaderContext(apiRequestContext.headers);
  const requiestedRegionId = headers.region ?? supportedRegions.US.id;
  const region = isValidCountryCode(requiestedRegionId) ? supportedRegions[requiestedRegionId] : supportedRegions.US;
  const requestedLocale = headers.locale ?? region.defaultLocale;
  const locale = retrieveLocale({
    region,
    locale: requestedLocale,
    defaultFallback: true
  });
  const language = retrieveLanguage({
    region,
    locale,
    defaultFallback: true
  });
  const currency = headers.currency ?? region.currency;
  return {
    region: region.id,
    locale,
    language,
    currency,
    name: headers.name
  };
}

const languageNamesByLocale = {
  "de-DE": "German",
  "en-035": "English",
  "en-145": "English",
  "en-150": "English",
  "en-AU": "English",
  "en-BR": "English",
  "en-CA": "English",
  "en-GB": "English",
  "en-US": "English",
  "es-419": "Spanish",
  "es-ES": "Spanish",
  "es-MX": "Spanish",
  "es-US": "Spanish",
  "fr-CA": "French",
  "fr-FR": "French",
  "ja-JP": "Japanese",
  "ko-KR": "Korean"
};

function figsShopLocalizationContextLink(shopLocalizationContext) {
  return setContext((_request, ctx) => {
    const headers = ctx.headers || {};
    serializeContextToHeaderTarget(shopLocalizationContext, {
      header: (field, value) => {
        headers[field] = value;
      }
    });
    return { ...ctx, headers };
  });
}

const apqEnabled = process.env.NEXT_PUBLIC_FIGS_ENV !== "local";
function figsLinkGenerator({ baseUrl, graph }) {
  return (shopLocalizationContext, serviceFilterPairsContext) => {
    const links = [
      figsAuthLink(),
      figsServiceSelectionLink(serviceFilterPairsContext),
      figsShopLocalizationContextLink(shopLocalizationContext),
      configuredHttpLink(baseUrl, graph)
    ];
    const link = ApolloLink.from(links);
    return link;
  };
}
function generateHash(query) {
  const definitions = query.definitions.filter((d) => d.kind === "OperationDefinition");
  const allQuery = definitions.every(
    (d) => d.kind === "OperationDefinition" && d.operation === "query"
  );
  if (allQuery) {
    return sha256(stripIgnoredCharacters(print(query)));
  }
  return sha256(print(query));
}
function configuredHttpLink(baseUrl, graph) {
  const httpLink = figsHttpLink(baseUrl, graph);
  if (apqEnabled) {
    return createPersistedQueryLink({
      useGETForHashedQueries: true,
      generateHash
    }).concat(httpLink);
  }
  return httpLink;
}

var ApolloClientProfile = /* @__PURE__ */ ((ApolloClientProfile2) => {
  ApolloClientProfile2[ApolloClientProfile2["FigsPublicSupergraph"] = 0] = "FigsPublicSupergraph";
  ApolloClientProfile2[ApolloClientProfile2["FigsAuthedSupergraph"] = 1] = "FigsAuthedSupergraph";
  return ApolloClientProfile2;
})(ApolloClientProfile || {});
function initializeClientFactory(figsApiBaseUrl) {
  const linksFor = (graph) => figsLinkGenerator({
    baseUrl: figsApiBaseUrl,
    graph
  });
  const linkGenerators = {
    [0 /* FigsPublicSupergraph */]: linksFor("catalog"),
    [1 /* FigsAuthedSupergraph */]: linksFor("shop")
  };
  const cacheConfigs = {
    [0 /* FigsPublicSupergraph */]: catalogCacheConfig,
    [1 /* FigsAuthedSupergraph */]: catalogCacheConfig
  };
  const config = {
    cacheConfigs,
    figsBaseUrl: figsApiBaseUrl,
    isServer,
    linkGenerators
  };
  return {
    createContext: (shopLocalizationContext, serviceFilterPairsContext) => createContext(config, shopLocalizationContext, serviceFilterPairsContext)
  };
}
function createContext(config, shopLocalizationContext, serviceFilterPairsContext, cache = /* @__PURE__ */ new Map()) {
  const ctx = shopLocalizationContext;
  return {
    for: (profile) => {
      return cachedClientOr(
        cache,
        profile,
        ctx,
        () => createClient(config, profile, ctx, serviceFilterPairsContext)
      );
    },
    // All clients that have been acquired via this factory.  Must have been acquired to be
    // in this list, this implementation is lazy.
    clients: () => selectClients(cache, ctx)
  };
}
function createClient(config, profile, shopLocalizationContext, serviceFilterPairsContext) {
  trace("createClient", profile.toString(), config);
  const cacheConfig = config.cacheConfigs[profile];
  const cache = new InMemoryCache(cacheConfig);
  const linkGenerator = config.linkGenerators[profile];
  const link = linkGenerator(shopLocalizationContext, serviceFilterPairsContext);
  return new ApolloClient({
    cache,
    defaultOptions: {
      watchQuery: {
        errorPolicy: "none"
      },
      query: {
        errorPolicy: "all"
      },
      mutate: {
        errorPolicy: "none"
      }
    },
    link,
    // profile-specific
    ssrMode: config.isServer
  });
}

function createExperimentationUserContext({
  visitorId,
  email,
  stylePreference,
  languagePreference,
  shoppingRegion,
  customerType,
  customProperties = {}
}) {
  return {
    key: visitorId,
    email,
    stylePreference,
    languagePreference,
    shoppingRegion,
    customerType,
    ...customProperties
  };
}
function createExperimentationOrganizationContext({
  id,
  name,
  customProperties = {}
}) {
  return {
    key: id,
    name,
    ...customProperties
  };
}
function createExperimentationAccountContext({
  userId,
  visitorId,
  customProperties = {}
}) {
  return {
    key: userId ?? visitorId,
    authentication: userId ? "authenticated" : "guest",
    ...customProperties
  };
}
function createExperimentationAppContext({
  name,
  version,
  customProperties = {}
}) {
  return {
    key: name,
    name,
    medium: "web",
    version,
    ...customProperties
  };
}
function createExperimentationContext({
  app,
  account,
  user,
  organization
}) {
  return {
    kind: "multi",
    app,
    account,
    user,
    ...organization ? { organization } : {}
  };
}

const isBrowser = typeof window !== "undefined" && typeof document !== "undefined";
const isServer = !isBrowser;

export { ApolloClientProfile, ServiceFilterPairs, buildContextForApiRequestHandler, buildContextForWebsitePageView, catalogCacheConfig, cookieDefaultOptions, createExperimentationAccountContext, createExperimentationAppContext, createExperimentationContext, createExperimentationOrganizationContext, createExperimentationUserContext, currencyKey, detectedCountryEchoKey, getFigsEnvironment, ingressDetectedCountryKey, initClient, initializeClientFactory, isBrowser, isServer, isValidCountryCode, languageNamesByLocale, legacyLocaleKey, localeKey, nameKey, regionKey, retrieveLocale, retrieveRegion, serializeContext, serializeContextToHeaderTarget, supportedRegions };
